import * as React from 'react';
import LooksTileUiKit from '@westwing/ui-kit/LooksTile';
import * as Styled from './LooksInfusionSlider.styles';
import { useGaTracking } from './useTracking';
import useInView from 'Client/hooks/dom/useInView';
import { Maybe } from 'Common/types/utility';
import useBellaTranslator from 'Client/hooks/useTranslator';
import { useBellaDispatch, useBellaSelector } from 'Client/redux/hooks';
import { setLDPKnownInfoAction } from 'Client/redux/lookDetail/actions';
import SliderTile, { SliderTileItem } from 'Client/components/Generic/ContentInfusionsSlider/SliderTile';
import ContentInfusionsSlider from 'Client/components/Generic/ContentInfusionsSlider';
import { looksSliderRatios, looksSliderLandscapeRatios } from 'Client/constants/aspectRatios';
import { Look } from 'Client/redux/productLooks/types';
import useFeatureToggle from 'Client/hooks/useFeatureToggle';
import { WishlistBadge } from 'Client/components/Looks/WishlistBadge';

interface LooksSliderProps {
    looks: Look[];
}

type EnhancedSliderTileItemType = SliderTileItem & { slug: string; trackingName: string };

const LooksSlider = ({ looks }: LooksSliderProps) => {
    const dispatch = useBellaDispatch();
    const { p100NewLooksWishlist } = useFeatureToggle();

    const t = useBellaTranslator();
    const sku = useBellaSelector(state => state.productdetails.productdetail.sku);

    const { trackLookSliderClicks, trackLookSliderImpression, trackLookSliderWishlist } = useGaTracking();

    const onLookClick = (look: Look) => {
        dispatch(setLDPKnownInfoAction(look));
        trackLookSliderClicks(`PDP: ${sku}, Look CTA: ${look.trackingName}`);
    };

    const containerRef = React.useRef<Maybe<HTMLDivElement>>(null);
    useInView({
        containerRef,
        options: { threshold: 0.5 },
        onVisibleCallback: () => trackLookSliderImpression(`PDP: ${sku}`),
    });

    const onWishlistBadgeClick = (newIsSelectedValue: boolean) => {
        if (newIsSelectedValue) {
            trackLookSliderWishlist(`Add Look to Wishlist`);
        } else {
            trackLookSliderWishlist(`Remove Look to Wishlist`);
        }
    };

    const mapToCards = (look: Look): EnhancedSliderTileItemType => {
        const details = {
            targetUrl: `/looks/${look.slug}`,
            fallbackSrc: look.genericImagePortrait?.file.url,
            desktopSrc: look.genericImagePortrait?.file.url || '',
            mobileSrc: look.genericImagePortrait?.file.url || '',
            borderColor: look.color || '',
            title: look.name,
            ctaText: t('Read and Shop'),
            onClick: () => onLookClick(look),
            isLandscape: false,
            slug: look.slug,
            trackingName: look.trackingName,
        };

        if (look.isLandscape && look.moodImage) {
            details.fallbackSrc = look.moodImage.file.url;
            details.desktopSrc = look.moodImage.file.url;
            details.mobileSrc = look.moodImage.file.url;
            details.isLandscape = true;
        }
        return details;
    };

    const cards = looks.map(mapToCards);

    return (
        <div ref={containerRef}>
            <Styled.LineSeparator />
            <ContentInfusionsSlider
                title={t('PDP_Look_Slider_Headline')}
                data-testid="looks-slider"
                showBorder={false}
                noSideMarginsForWideScreen={false}
            >
                {cards.map((look: EnhancedSliderTileItemType, index: number) => (
                    <SliderTile
                        ratio={look.isLandscape ? looksSliderLandscapeRatios : looksSliderRatios}
                        card={look}
                        key={look.title}
                        showBorder={index === 0}
                    >
                        {p100NewLooksWishlist && (
                            <LooksTileUiKit.TopRightBadge>
                                <WishlistBadge
                                    lookSlug={look.slug}
                                    testId="look-slider-wishlist-badge"
                                    onClick={onWishlistBadgeClick}
                                />
                            </LooksTileUiKit.TopRightBadge>
                        )}
                    </SliderTile>
                ))}
            </ContentInfusionsSlider>
        </div>
    );
};

export default LooksSlider;
